<template>
  <div>
    <b-loading :is-full-page="true" :active="!initialized"></b-loading>
    <section v-if="initialized" class="section is-main-section">
      <CardComponent>
        <div class="section has-text-centered">
          <h1 class="title">
            {{
              added
                ? 'Create New Channel Viewing Slot'
                : 'Edit Channel Viewing Slot'
            }}
          </h1>
        </div>
        <div class="columns is-vcentered">
          <div class="column is-8 is-offset-2">
            <div class="flex-card light-bordered light-raised">
              <div class="card-body">
                <ValidationObserver ref="observer" v-slot="{ passes, errors }">
                  <form @submit.prevent="passes(submit)">
                    <div class="columns is-multiline">
                      <div class="column is-12">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Name"
                            rules="required"
                          >
                            <b-field
                              label="Name"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-input v-model="item.name" />
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>
                      <div class="column is-6">
                        <div class="control">
                          <BaseTimePicker
                            v-model="item.start_time"
                            label="Start Time"
                            :increment-minutes="15"
                            rules="required"
                          />
                        </div>
                      </div>
                      <div class="column is-6">
                        <div class="control">
                          <BaseTimePicker
                            v-model="item.end_time"
                            label="End Time"
                            :increment-minutes="15"
                            rules="required"
                          />
                        </div>
                      </div>
                      <div class="column is-12">
                        <div class="control">
                          <b-field label="Days">
                            <div class="block">
                              <b-checkbox
                                v-for="day in days"
                                :key="day.value"
                                v-model="item.days"
                                :native-value="day.value"
                              >
                                {{ day.day }}
                              </b-checkbox>
                            </div>
                          </b-field>
                        </div>
                      </div>
                      <div class="column is-12">
                        <b-message type="is-info">
                          <strong>Slot Groups:</strong> Link multiple slots
                          together (e.g., "Weekday Prime", "Weekday Prime Repeat
                          1", "Weekday Prime Repeat 2") by giving them the same
                          Group ID.
                        </b-message>
                      </div>
                      <div class="column is-8">
                        <div class="control">
                          <b-field label="Slot Group ID (optional)">
                            <b-select
                              v-if="!showCustomGroupInput"
                              v-model="item.slot_group_id"
                              placeholder="Select existing group or create new"
                              expanded
                            >
                              <option :value="null">No Group</option>
                              <option
                                v-for="group in existingGroups"
                                :key="group"
                                :value="group"
                              >
                                {{ group }}
                              </option>
                              <option value="__new__"
                                >+ Create New Group</option
                              >
                            </b-select>
                            <b-input
                              v-else
                              v-model="item.slot_group_id"
                              placeholder="e.g., weekday-prime-group"
                            />
                          </b-field>
                          <p class="help">
                            <a
                              v-if="!showCustomGroupInput"
                              @click.prevent="
                                showCustomGroupInput = true
                                item.slot_group_id = null
                              "
                            >
                              Or enter custom group ID
                            </a>
                            <a
                              v-else
                              @click.prevent="
                                showCustomGroupInput = false
                                item.slot_group_id = null
                              "
                            >
                              ← Back to existing groups
                            </a>
                          </p>
                        </div>
                      </div>
                      <div class="column is-2">
                        <div class="control">
                          <b-field label="Sequence">
                            <b-input
                              v-model.number="item.sequence_order"
                              type="number"
                              min="0"
                              placeholder="0"
                            />
                          </b-field>
                          <p class="help">0=Prime, 1+=Repeat</p>
                        </div>
                      </div>
                      <div class="column is-2">
                        <div class="control">
                          <b-field label="Days After Prev">
                            <b-input
                              v-model.number="item.days_after_previous"
                              type="number"
                              min="0"
                              max="7"
                              placeholder="1"
                            />
                          </b-field>
                          <p class="help">Days after previous slot</p>
                        </div>
                      </div>
                    </div>
                    <div class="has-text-centered">
                      <div class="is-block has-margin-bottom-15">
                        <b-button
                          icon-left="arrow-left"
                          class="is-medium has-margin-right-10"
                          tag="router-link"
                          :to="{
                            name: 'ManageChannelViewingSlots',
                            params: {
                              id: !added ? item.channel_id : $route.params.id
                            }
                          }"
                        >
                          Back to List</b-button
                        >
                        <button
                          class="button is-primary is-medium btn-align no-lh raised primary-btn"
                          type="submit"
                          :disabled="loading"
                          :class="{ 'is-loading': loading }"
                        >
                          {{ added ? 'Create' : 'Update' }}
                        </button>
                      </div>
                    </div>
                  </form>
                </ValidationObserver>
              </div>
            </div>
          </div>
        </div>
      </CardComponent>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { format } from 'date-fns'
import CardComponent from '@/components/CardComponent'
import BaseTimePicker from '@/components/BaseTimePicker'
import ChannelViewingSlotApi from '@/api/channelViewingSlots'

/**
 * Parse TIME string (HH:mm:ss) to Date object in local timezone
 * Avoids UTC interpretation that causes timezone shift
 */
function parseTimeOnly(timeStr) {
  if (!timeStr) return null

  // Already a Date object
  if (timeStr instanceof Date) return timeStr

  // TIME string format: HH:mm or HH:mm:ss
  if (typeof timeStr === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(timeStr)) {
    const [hours, minutes, seconds] = timeStr.split(':')
    const date = new Date()
    date.setHours(parseInt(hours, 10))
    date.setMinutes(parseInt(minutes, 10))
    date.setSeconds(seconds ? parseInt(seconds, 10) : 0)
    date.setMilliseconds(0)
    return date
  }

  // Full datetime string - parse normally
  return new Date(timeStr)
}

/**
 * Format Date to TIME string (HH:mm:ss) for saving
 */
function formatTimeOnly(date) {
  if (!date) return null
  if (typeof date === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(date)) {
    return date.length === 5 ? `${date}:00` : date
  }
  return format(new Date(date), 'HH:mm:ss')
}

export default {
  name: 'EditChannelViewingSlot',
  components: {
    CardComponent,
    BaseTimePicker
  },
  metaInfo: {
    title: 'Edit Channel Viewing Slot'
  },
  computed: {
    ...mapGetters(['user'])
  },
  watch: {
    'item.slot_group_id'(newVal) {
      if (newVal === '__new__') {
        this.showCustomGroupInput = true
        this.item.slot_group_id = null
      }
    }
  },
  async mounted() {
    this.added = this.$route.name === 'CreateChannelViewingSlot' ? true : false
    if (!this.added) {
      await this.setData()
      // After loading the item, we have channel_id, now load groups
      await this.loadExistingGroups()
    } else {
      // For create mode, channel_id is in route params
      await this.loadExistingGroups()
    }
    this.initialized = true
  },
  data() {
    return {
      loading: false,
      initialized: false,
      added: false,
      item: {
        days: []
      },
      days: [
        { value: 1, day: 'Monday' },
        { value: 2, day: 'Tuesday' },
        { value: 3, day: 'Wednesday' },
        { value: 4, day: 'Thursday' },
        { value: 5, day: 'Friday' },
        { value: 6, day: 'Saturday' },
        { value: 7, day: 'Sunday' }
      ],
      existingGroups: [],
      showCustomGroupInput: false
    }
  },
  methods: {
    ...mapActions(['getUser']),
    async loadExistingGroups() {
      try {
        // For CreateChannelViewingSlot route, channel_id is in params.id
        // For EditChannelViewingSlot route, we need to get it from the item
        let channelId = this.$route.params.id

        // If editing existing slot and item has channel_id, use that
        if (this.item && this.item.channel_id) {
          channelId = this.item.channel_id
        }

        if (!channelId) {
          console.warn('No channel ID found for loading groups')
          return
        }

        const allSlots = await ChannelViewingSlotApi.getByChannel(channelId)
        const groups = new Set()

        allSlots.forEach(slot => {
          if (slot.slot_group_id) {
            groups.add(slot.slot_group_id)
          }
        })

        this.existingGroups = Array.from(groups).sort()
        console.log('Loaded existing groups:', this.existingGroups)
      } catch (error) {
        console.error('Failed to load existing groups:', error)
      }
    },
    async setData(id) {
      this.initialized = false
      this.added = false
      this.item = await ChannelViewingSlotApi.getById(
        id || this.$route.params.id
      )
      if (this.item) {
        // Use parseTimeOnly to handle TIME strings from database
        this.item.start_time = parseTimeOnly(this.item.start_time)
        this.item.end_time = parseTimeOnly(this.item.end_time)
        this.item.days = this.item.days.split(',')

        // Check if current group ID is in existing groups or is custom
        if (
          this.item.slot_group_id &&
          !this.existingGroups.includes(this.item.slot_group_id)
        ) {
          this.showCustomGroupInput = true
        }
      }
      this.initialized = true
    },
    async submit() {
      this.loading = true

      // Format times as TIME strings before sending to backend
      const payload = {
        ...this.item,
        start_time: formatTimeOnly(this.item.start_time),
        end_time: formatTimeOnly(this.item.end_time)
      }

      if (!this.item.id) {
        try {
          payload.channel_id = this.$route.params.id
          let response = await ChannelViewingSlotApi.create(payload)
          this.$buefy.toast.open({
            message: 'Channel Viewing Slot created successfully!',
            position: 'is-top',
            type: 'is-success'
          })
          this.$router.push({
            name: 'EditChannelViewingSlot',
            params: { id: response.id }
          })
          await this.setData(response.id)
          this.loading = false
        } catch (err) {
          console.log(err)
          this.$buefy.toast.open({
            message: 'Error saving channel viewing slot',
            position: 'is-top',
            type: 'is-danger'
          })
          this.loading = false
        }
      } else {
        try {
          await ChannelViewingSlotApi.update(payload)
          this.$buefy.toast.open({
            message: 'Channel Viewing Slot updated successfully!',
            position: 'is-top',
            type: 'is-success'
          })
          await this.setData()
          this.loading = false
        } catch (err) {
          console.log(err)
          this.$buefy.toast.open({
            message: 'Error saving channel viewing slot',
            position: 'is-top',
            type: 'is-danger'
          })
          this.loading = false
        }
      }
    }
  }
}
</script>
